/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable operator-linebreak */
import _ from 'lodash';
import {
  getBlogPost,
  getBlogs,
  getRelatedBlogs,
  getPostLink
} from 'adapters/albumAdapter';
import {
  Twitter, Pinterest, Facebook, Reddit
} from 'react-social-sharing';
import { PATH_BLOG } from 'constants';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import classNames from 'classnames';
import HeroLayout from 'layouts/HeroLayout';
import Grid from '@mui/material/Grid';
import Image from 'components/Image';
import Link from 'next/link';
import makeClasses from 'hooks/useClasses';
import moment from 'moment';
import PageHead from 'components/Head/PageHead';
import PropTypes from 'prop-types';
import React from 'react';
import RichTextRenderer from 'components/Text/RichTextRenderer';
import Stack from '@mui/material/Stack';
import styles from 'styles/jss/pages/blogPostStyle';
import Typography from '@mui/material/Typography';

const getSchemaJSON = (content) => {
  const images = content.body.reduce((memo, block) => {
    if (block.type === 'image') {
      memo.push(_.get(block, 'data.file'));
    }
    return memo;
  }, []);

  return {
    '@context': 'https://schema.org/',
    '@type': 'NewsArticle',
    headline: content.title,
    image: [_.get(content, 'heroImage.url'), ...images],
    datePublished: moment(content.publishDate).toISOString(),
    dateModified: moment(content.modifiedDate).toISOString()
  };
};

export const getStaticPaths = async () => {
  const response = await getBlogs();
  const paths = response.map((blog) => ({
    params: {
      postSlug: blog.slug,
      postId: blog.entryId
    }
  }));
  return {
    paths,
    fallback: 'blocking'
  };
};

export const getStaticProps = async (context) => {
  const entryId = _.get(context, 'params.postId');
  const slug = _.get(context, 'params.postSlug');
  const content = await getBlogPost(entryId);
  const relatedBlogs = await getRelatedBlogs(entryId, 2);
  const blogUrl = getPostLink(slug, entryId);
  const schemaJSON = getSchemaJSON(content, blogUrl, slug);
  return {
    props: {
      content, relatedBlogs, blogUrl, schemaJSON
    }
  };
};

const BlogPost = ({
  content, relatedBlogs, blogUrl, schemaJSON
}) => {
  const classes = makeClasses(styles);
  const sidebarWidth = 300;
  if (!content) return null;
  let contentBody = content.body;

  const { sidebarAds, inlineFooterAds, inlinePostIntroAds } = _.get(
    content,
    'ads',
    []
  ).reduce(
    (memo, ad) => {
      if (ad.location === 'sidebar') {
        memo.sidebarAds.push(ad);
      } else if (ad.location === 'inlineFooter') {
        memo.inlineFooterAds.push(ad);
      } else if (ad.location === 'inlinePostIntro') {
        memo.inlinePostIntroAds.push(ad);
      }
      return memo;
    },
    {
      sidebarAds: [],
      inlinePostIntroAds: [],
      inlineFooterAds: []
    }
  );

  if (inlinePostIntroAds.length > 0) {
    contentBody = [
      content.body[0],
      {
        type: 'inlinePostIntro',
        data: inlinePostIntroAds[0]
      },
      ...contentBody.slice(1)
    ];
  }
  const { heroAttributionUrl, heroAttributionName } = content.heroImage;
  return (
    <>
      <PageHead
        title={`${_.get(content, 'title')} | Scribble`}
        description={_.get(content, 'body.summary')}
        image={_.get(content, 'heroImage.url')}
      >
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaJSON)
          }}
          key="product-jsonld"
        />
      </PageHead>
      <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
        <Link underline="hover" color="inherit" href={PATH_BLOG}>
          Scribble Blog
        </Link>
        <Typography color="text.primary">{content.title}</Typography>
      </Breadcrumbs>
      <Grid
        item
        xs={12}
        md={9}
        sx={{
          pr: { xs: 0, md: 5 }
        }}
      >
        <Typography
          component="h1"
          variant="pageTitle"
          className={classes.blogTitle}
        >
          {content.title}
        </Typography>
        <Stack>
          <Box>
            <Typography className={classes.byline}>
              {moment(content.publishDate).format('MMMM Do YYYY')}
              {/* {' | Words by '}
              {content.author.name} */}
              {heroAttributionUrl && heroAttributionName && (
                <>
                  {' | Hero Image by '}
                  <Link href={content.heroImage.heroAttributionUrl} passHref>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a target="_blank">
                      {content.heroImage.heroAttributionName}
                    </a>
                  </Link>
                </>
              )}
            </Typography>
          </Box>
          <Box className={classes.socialShare}>
            <Pinterest solidcircle small link={blogUrl} />
            <Twitter solidcircle small link={blogUrl} />
            <Facebook solidcircle small link={blogUrl} />
            <Reddit solidcircle small link={blogUrl} />
          </Box>
        </Stack>
        <Box className={classes.blogContent}>
          <RichTextRenderer data={contentBody} />
          {inlineFooterAds &&
            inlineFooterAds.map((ad) => (
              <span key={ad.entryId}>
                <RichTextRenderer data={ad.body} />
                <Link passHref href={ad.url}>
                  <Button
                    component="a"
                    variant="contained"
                    size="large"
                    sx={{ marginBottom: 10 }}
                  >
                    {ad.buttonLabel
                      ? ad.buttonLabel
                      : 'Build your scribble book now'}
                  </Button>
                </Link>
              </span>
            ))}
        </Box>
      </Grid>
      <Grid container xs={12} md={3} className={classes.sidebar} item>
        {sidebarAds &&
          sidebarAds.map((ad) => (
            <Box
              className={classNames('cta', ad.template, classes.sidebarBox)}
              key={ad.entryId}
            >
              <Box position="relative">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href={ad.url}>
                  <Image
                    src={`${ad.image.url}?width=${sidebarWidth}`}
                    layout="responsive"
                    width={ad.image.width}
                    height={ad.image.height}
                    priority
                    objectFit="cover"
                    alt={ad.title}
                  />
                </a>
              </Box>
            </Box>
          ))}
        {relatedBlogs &&
          relatedBlogs.map((blog) => (
            <Card className={classes.blogHomeCard} key={blog.entryId}>
              <CardActionArea href={blog.url}>
                <CardMedia
                  component="img"
                  height="150"
                  image={`${blog.heroImage.url}?width=${sidebarWidth}`}
                  alt={blog.heroImage.imageTitle}
                />
                <CardContent>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="div"
                    className="blogTitle"
                  >
                    {blog.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="blogSummary"
                  >
                    {blog.summary}
                  </Typography>
                  <Button
                    disableRipple
                    sx={{
                      '&:hover': {
                        background: 'none'
                      }
                    }}
                  >
                    Read More
                  </Button>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
      </Grid>
    </>
  );
};

BlogPost.getLayout = (page) => (
  <HeroLayout
    heroImage={_.get(page, 'props.content.heroImage.url')}
    heroImageTitle={_.get(page, 'props.content.heroImage.imageTitle')}
  >
    {page}
  </HeroLayout>
);

BlogPost.defaultProps = {
  relatedBlogs: []
};

BlogPost.propTypes = {
  blogUrl: PropTypes.string.isRequired,
  content: PropTypes.shape().isRequired,
  relatedBlogs: PropTypes.arrayOf(PropTypes.shape()),
  schemaJSON: PropTypes.shape().isRequired
};

export default BlogPost;
